import { useState } from "react";
import { useShellController } from "../controllers/useShellController";
import { useAccount } from "../controllers/useAccount";
import { useProjectNav } from "../controllers/useProjectNav";
import { TitleBar } from "./shell/TitleBar";
import { ProjectRail } from "./shell/ProjectRail";
import { Sidebar } from "./shell/Sidebar";
import { ResizeHandle } from "./shell/ResizeHandle";
import { AppDetail } from "./screens/AppDetail";
import { RunningApp } from "./screens/RunningApp";
import { ProjectExtApps } from "./screens/ProjectExtApps";
import { AdminView } from "./screens/AdminView";
import { DownloadsView } from "./screens/DownloadsView";
import { AccountView } from "./screens/AccountView";
import { MyActivityView } from "./screens/MyActivityView";
import { HomeView } from "./screens/HomeView";
import { Onboarding } from "./screens/Onboarding";
import { ProjectDetailView } from "./screens/ProjectDetailView";
import { EqmsSectionRouter } from "./screens/eqms/EqmsView";
import { WorkspaceChatView } from "./screens/WorkspaceChatView";
import { WorkspaceDetailView } from "./screens/WorkspaceDetailView";
import { InvitationsView } from "./screens/InvitationsView";
import { CreateWorkspaceWizard } from "./screens/CreateWorkspaceWizard";
import { CreateProjectWizard } from "./screens/CreateProjectWizard";

type Shell = ReturnType<typeof useShellController>;

export function App() {
  const c = useShellController();
  const { status, principal } = c;

  if (status === "loading") return <div className="center">불러오는 중…</div>;
  if (status === "unauthenticated" || !principal) return <div className="center">로그인으로 이동 중…</div>;

  return <AuthedApp c={c} principal={principal} />;
}

function AuthedApp({ c, principal }: { c: Shell; principal: NonNullable<Shell["principal"]> }) {
  const acc = useAccount();
  const nav = useProjectNav();
  const [showCreateWs, setShowCreateWs] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  // First-login onboarding gate. (All hooks must run before any early return.)
  if (acc.loading && !acc.account) return <div className="center">불러오는 중…</div>;
  if (acc.account && !acc.account.onboarded) {
    return <Onboarding acc={acc} isAdmin={c.admin} />;
  }

  const active = nav.active;
  const appView = active.kind === "shell" && active.view === "apps";
  // 프로젝트 관리 leaf is for those who administer the workspace's projects (workspace
  // ADMIN/owner), not platform superadmins — the 관리자 rail button keeps using c.admin.
  const wsAdmin = nav.ws.members.some((m) => m.userId === acc.account?.id && m.role === "ADMIN");

  // Workspace registration wizard: on success, refresh the list and switch to the new workspace.
  async function handleWorkspaceCreated(ws: { id: string }, failedInvites: string[]) {
    setShowCreateWs(false);
    await nav.ws.reloadWorkspaces();
    nav.selectWorkspace(ws.id);
    if (failedInvites.length > 0) {
      window.alert(`워크스페이스를 만들었어요. 일부 초대는 실패했습니다: ${failedInvites.join(", ")}`);
    }
  }

  // Project creation wizard: on success, refresh the workspace's projects and open the new project.
  async function handleProjectCreated(project: { id: string; name: string }, failedMembers: string[]) {
    setShowCreateProject(false);
    const wid = nav.ws.workspace?.id;
    if (wid) nav.ws.openWorkspace(wid);
    nav.openLeaf(project.id, { kind: "tab", tab: "overview" }, `${project.name} · 개요`);
    if (failedMembers.length > 0) {
      window.alert(`프로젝트를 만들었어요. 일부 멤버 추가는 실패했습니다: ${failedMembers.join(", ")}`);
    }
  }

  const crumb =
    active.kind === "project" ? active.label
    : active.view === "home" ? "홈"
    : active.view === "myActivity" ? "내 활동"
    : active.view === "downloads" ? "다운로드"
    : active.view === "chat" ? "채팅"
    : active.view === "workspace" ? `${nav.ws.workspace?.name ?? "워크스페이스"} · 관리`
    : active.view === "invitations" ? "내 초대"
    : active.view === "account" ? "내 계정"
    : active.view === "admin" ? "관리자"
    : "앱";

  return (
    <div className="win">
      <TitleBar
        principal={principal}
        crumb={crumb}
        onToggleNav={c.toggleNav}
        workspaces={nav.ws.workspaces}
        currentWorkspaceId={nav.ws.workspace?.id ?? null}
        onSelectWorkspace={nav.selectWorkspace}
        onOpenCreateWorkspace={() => setShowCreateWs(true)}
        shellView={active.kind === "shell" ? active.view : null}
        onShellView={(v) => {
          nav.showShell(v);
          // 앱 additionally resets the marketplace pane (closes a running app) the way the old rail did.
          if (v === "apps") c.changeView("explore");
          else c.closeApp();
          c.closeNav();
        }}
      />
      <div className={`body ${appView ? "apps" : ""} ${c.navOpen ? "nav-open" : ""}`}>
        <ProjectRail nav={nav} c={c} wsAdmin={wsAdmin} onCreateProject={() => setShowCreateProject(true)} />
        <ResizeHandle varName="--rail-w" storageKey="compath.railW" def={244} min={180} max={440} style={{ left: "var(--rail-w, 244px)" }} />
        <div className="nav-backdrop" onClick={c.closeNav} />
        {appView && (
          <Sidebar
            apps={c.apps}
            query={c.query}
            onQuery={c.setQuery}
            selectedId={c.selectedId}
            admin={c.admin}
            onSelect={c.selectApp}
            onOpen={c.openApp}
            onLink={c.goToLink}
          />
        )}
        {appView && (
          <ResizeHandle varName="--side-w" storageKey="compath.sideW" def={320} min={230} max={560} style={{ left: "calc(var(--rail-w, 244px) + var(--side-w, 320px))" }} />
        )}
        <main className="main">
          <MainArea c={c} nav={nav} principal={principal} currentUserId={acc.account?.id ?? null} />
        </main>
      </div>
      {showCreateWs && (
        <CreateWorkspaceWizard
          getToken={c.getToken}
          onClose={() => setShowCreateWs(false)}
          onCreated={handleWorkspaceCreated}
        />
      )}
      {showCreateProject && nav.ws.workspace && (
        <CreateProjectWizard
          getToken={c.getToken}
          workspaceId={nav.ws.workspace.id}
          members={nav.ws.members}
          currentUserId={acc.account?.id ?? null}
          onClose={() => setShowCreateProject(false)}
          onCreated={handleProjectCreated}
        />
      )}
    </div>
  );
}

function MainArea({ c, nav, principal, currentUserId }: {
  c: Shell;
  nav: ReturnType<typeof useProjectNav>;
  principal: NonNullable<Shell["principal"]>;
  currentUserId: string | null;
}) {
  const active = nav.active;

  if (active.kind === "project") {
    const ready = nav.ws.project?.id === active.projectId && !nav.ws.loadingProject;
    if (active.leaf.kind === "tab") {
      if (active.leaf.tab === "extapps") {
        return (
          <div className="eqms-page">
            <ProjectExtApps apps={c.apps} transport={c.transport} principal={principal} projectName={nav.ws.project?.name ?? ""} />
          </div>
        );
      }
      if (!ready) return <div className="center">불러오는 중…</div>;
      return <div className="eqms-page"><ProjectDetailView ws={nav.ws} initialTab={active.leaf.tab} currentUserId={currentUserId} /></div>;
    }
    // eqms leaf
    if (nav.eqms.projectId !== active.projectId) return <div className="center">불러오는 중…</div>;
    return (
      <div className="eqms-page">
        <EqmsSectionRouter eqms={nav.eqms} members={nav.ws.projectMembers} currentUserId={currentUserId} />
      </div>
    );
  }

  switch (active.view) {
    case "home":
      return <HomeView nav={nav} userName={principal.name} />;
    case "downloads":
      // 자료실: platform-wide, not workspace- or project-scoped, so it lives in the title bar's top
      // menu next to 앱 rather than under a project in the rail.
      return <div className="eqms-page"><DownloadsView admin={c.admin} /></div>;
    case "myActivity":
      return <MyActivityView />;
    case "chat":
      return <WorkspaceChatView workspaceId={nav.ws.workspace?.id ?? null} members={nav.ws.members} currentUserId={currentUserId} />;
    case "invitations":
      // Invitations are addressed to the PERSON, not to the current workspace — so this is its own
      // rail entry rather than a tab under 워크스페이스 관리 (which a non-member cannot even open).
      return <InvitationsView ws={nav.ws} onBack={() => nav.showShell("home")} />;
    case "workspace":
      // Reuses the nav's own useWorkspaces instance (already holding this workspace's members,
      // invitations and projects) rather than mounting a second copy of the controller.
      if (!nav.ws.workspace) return <div className="center">워크스페이스를 선택하세요</div>;
      return (
        <div className="eqms-page">
          <WorkspaceDetailView
            ws={nav.ws}
            onBack={() => nav.showShell("home")}
            onOpenProject={(p) => nav.openLeaf(p.id, { kind: "tab", tab: "overview" }, `${p.name} · 개요`)}
          />
        </div>
      );
    case "account":
      return <AccountView />;
    case "admin":
      return <AdminView principal={principal} getToken={c.getToken} onLinked={c.onLinked} />;
    case "apps":
    default:
      return c.open ? (
        <RunningApp open={c.open} transport={c.transport} principal={principal} onClose={c.closeApp} />
      ) : c.selected ? (
        <AppDetail app={c.selected} onOpen={() => c.openApp(c.selected!)} />
      ) : (
        <div className="center">왼쪽에서 앱을 선택하세요</div>
      );
  }
}
